export type LiveUser = {
  id: string;
  displayName: string;
};

type LiveEventBase = {
  id: string;
  roomId: string;
  user: LiveUser;
  at: number;
};

export type LiveEvent =
  | (LiveEventBase & { type: "comment"; text: string })
  | (LiveEventBase & { type: "like"; count: number })
  | (LiveEventBase & { type: "gift"; giftId: string; giftName: string; count: number; value: number })
  | (LiveEventBase & { type: "follow" });

export type LiveSourceStatus = "idle" | "connecting" | "connected" | "offline";

/** Transport-independent sink consumed by mocks, WebSockets and future platform SDKs. */
export interface LiveEventSink {
  publish(event: LiveEvent): void;
}

/** A platform transport owns connectivity only; it never knows game rules. */
export interface LiveEventSource {
  readonly sourceId: string;
  start(): void | Promise<void>;
  stop(): void | Promise<void>;
}

/** Converts one platform's callback envelope into the stable live event contract. */
export interface LivePlatformAdapter<Input> {
  readonly platform: string;
  normalize(input: Input): LiveEvent[];
}

export type BarrageCommand = "left" | "right" | "jump";

const LEFT_WORDS = ["左", "left", "l", "←", "往左", "左边"];
const RIGHT_WORDS = ["右", "right", "r", "→", "往右", "右边"];
const JUMP_WORDS = ["跳", "jump", "j", "↑", "冲", "起跳"];

export function parseBarrageCommand(text: string): BarrageCommand | null {
  const normalized = text.trim().toLocaleLowerCase("zh-CN").replace(/[!！。,.，]/g, "");
  if (LEFT_WORDS.some((word) => normalized === word || normalized.includes(word))) return "left";
  if (RIGHT_WORDS.some((word) => normalized === word || normalized.includes(word))) return "right";
  if (JUMP_WORDS.some((word) => normalized === word || normalized.includes(word))) return "jump";
  return null;
}

export function isLiveEvent(value: unknown): value is LiveEvent {
  if (!value || typeof value !== "object") return false;
  const event = value as Record<string, unknown>;
  if (typeof event.id !== "string" || typeof event.roomId !== "string" || typeof event.at !== "number") return false;
  if (!event.user || typeof event.user !== "object") return false;
  const user = event.user as Record<string, unknown>;
  if (typeof user.id !== "string" || typeof user.displayName !== "string") return false;
  if (event.type === "comment") return typeof event.text === "string" && event.text.length <= 80;
  if (event.type === "like") return typeof event.count === "number" && event.count > 0 && event.count <= 10_000;
  if (event.type === "gift") {
    return typeof event.giftId === "string"
      && typeof event.giftName === "string"
      && typeof event.count === "number"
      && typeof event.value === "number"
      && event.count > 0
      && event.count <= 100
      && event.value >= 0;
  }
  return event.type === "follow";
}

let sequence = 0;

type LiveEventInput = LiveEvent extends infer Event
  ? Event extends LiveEvent
    ? Omit<Event, "id" | "roomId" | "user" | "at">
    : never
  : never;

export function createLiveEvent(
  event: LiveEventInput,
  user: LiveUser = { id: "mock-viewer", displayName: "模拟观众" },
): LiveEvent {
  sequence += 1;
  return {
    ...event,
    id: `local-${Date.now()}-${sequence}`,
    roomId: "1000000000000000001",
    user,
    at: Date.now(),
  } as LiveEvent;
}

export class LiveEventHub implements LiveEventSink {
  readonly #listeners = new Set<(event: LiveEvent) => void>();

  subscribe(listener: (event: LiveEvent) => void): () => void {
    this.#listeners.add(listener);
    return () => this.#listeners.delete(listener);
  }

  publish(event: LiveEvent): void {
    for (const listener of this.#listeners) listener(event);
  }
}
